<template>
  <div class="mx-auto px-4 py-4 w-[90%]">
    <!-- Шапка -->
    <div class="flex items-center justify-between mb-4">
      <div>
        <button type="button" @click="goBack" class="text-sm text-blue-600 hover:underline mb-1">← К реестру</button>
        <h1 class="text-lg font-semibold text-gray-800">ФР-УНП-01. Контролируемое использование. Версия 01</h1>
      </div>
      <span :class="statusClass(doc.status)" class="text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
        {{ statusLabel(doc.status) }}
      </span>
    </div>

    <div v-if="loading" class="text-sm text-gray-500">Загрузка...</div>

    <div v-else class="space-y-6">
      <!-- Шапка документа -->
      <section class="bg-white border border-gray-200 rounded-lg p-4 grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm text-gray-700 mb-1">Номер КИ</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ doc.number || '—' }}</span>
          <input
            v-else
            v-model="doc.number"
            type="text"
            placeholder="КИ-..."
            class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Дата инициирования</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ initiatedDisplay }}</span>
          <input
            v-else
            :value="initiatedDisplay"
            type="text"
            disabled
            class="w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 h-[39px]"
          />
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Источник документа</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ originDisplay }}</span>
          <input
            v-else
            :value="originDisplay"
            type="text"
            disabled
            class="w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 h-[39px]"
          />
        </div>
        <div class="col-span-2">
          <label class="block text-sm text-gray-700 mb-1">Причина инициирования КИ</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.cause || '—' }}</span>
          <textarea
            v-else
            v-model="doc.cause"
            rows="2"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>
      </section>

      <!-- Затронутые продукты/материалы -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <h2 class="font-semibold text-gray-800 mb-3">Затронутые продукты/материалы</h2>
        <table class="w-full text-sm border border-gray-200 table-fixed">
          <colgroup>
            <col style="width: 4%" />
            <col style="width: 48%" />
            <col style="width: 30%" />
            <col style="width: 12%" />
            <col style="width: 6%" />
          </colgroup>
          <thead class="bg-gray-50">
            <tr>
              <th class="px-2 py-1 text-center border-b">№</th>
              <th class="px-2 py-1 text-left border-b">Номер p/n</th>
              <th class="px-2 py-1 text-left border-b">Краткое название</th>
              <th class="px-2 py-1 text-left border-b">Количество</th>
              <th class="px-2 py-1 border-b"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="items.length === 0">
              <td colspan="5" class="px-2 py-2 text-gray-400 text-center">Нет строк</td>
            </tr>
            <template v-for="(item, index) in items" :key="index">
            <tr class="border-t border-gray-200">
              <td class="px-2 py-1 align-top text-center text-gray-500">{{ index + 1 }}</td>
              <td class="px-2 py-1 align-top">
                <span v-if="isReadOnly" class="text-sm text-gray-700">{{ item.product_name || '—' }}</span>
                <div v-else class="flex items-start gap-1">
                  <div class="flex-1 min-w-0">
                    <div v-if="!item.manual" class="v-select-filter" style="width: 100%; flex: none;">
                      <v-select
                        v-model="item.selected"
                        :options="productResults"
                        :filterable="false"
                        :loading="productLoading"
                        :get-option-label="productLabel"
                        label="name"
                        placeholder="Выберите из номенклатуры"
                        :disabled="isReadOnly"
                        @search="(q) => onProductSearch(q, index)"
                        @open="() => onProductSearch('', index)"
                        @update:modelValue="(val) => onProductSelected(index, val)"
                      >
                        <template #option="o">
                          <span>{{ productLabel(o) }}</span>
                        </template>
                        <template #no-options>
                          <span v-if="productLoading">Загрузка...</span>
                          <span v-else>Ничего не найдено</span>
                        </template>
                        <template #list-footer>
                          <li v-if="productHasMore" class="px-3 py-2 text-center border-t">
                            <button
                              type="button"
                              @click.prevent.stop="loadMoreProducts"
                              :disabled="productLoading"
                              class="text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                            >
                              {{ productLoading ? 'Загрузка...' : 'Загрузить ещё...' }}
                            </button>
                          </li>
                        </template>
                      </v-select>
                    </div>
                    <input
                      v-else
                      v-model="item.product_name"
                      :disabled="isReadOnly"
                      type="text"
                      placeholder="Ручной ввод"
                      class="w-full border border-gray-300 rounded-lg px-3 text-sm disabled:bg-gray-100 h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    v-if="!isReadOnly"
                    type="button"
                    @click="toggleManual(index)"
                    :title="item.manual ? 'Выбрать из номенклатуры' : 'Ввести вручную'"
                    class="flex-shrink-0 h-[39px] w-8 inline-flex items-center justify-center border border-gray-300 rounded text-gray-500 hover:bg-gray-50"
                  >
                    {{ item.manual ? '☰' : '✎' }}
                  </button>
                </div>
              </td>
              <td class="px-2 py-1 align-top">
                <span v-if="isReadOnly" class="text-sm text-gray-700">{{ item.short_name || '—' }}</span>
                <input
                  v-else
                  v-model="item.short_name"
                  type="text"
                  class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </td>
              <td class="px-2 py-1 align-top">
                <span v-if="isReadOnly" class="text-sm text-gray-700">{{ item.quantity ?? '—' }}</span>
                <input
                  v-else
                  v-model="item.quantity"
                  type="number"
                  step="0.001"
                  class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </td>
              <td class="px-2 py-1 align-top text-center">
                <button
                  v-if="!isReadOnly"
                  type="button"
                  @click="removeItem(index)"
                  class="h-[39px] text-red-600 hover:text-red-800"
                  title="Удалить строку"
                >
                  ✕
                </button>
              </td>
            </tr>
            <!-- Привязка строки к конкретному изделию (ТЗ-7.3): необязательна -->
            <tr class="bg-gray-50">
              <td class="px-2 pb-2 text-gray-400"></td>
              <td class="px-2 pb-2" colspan="4">
                <div class="text-xs font-semibold text-gray-600 mb-1">Привязка к изделию</div>
                <ProductBindingSelector
                  v-model="item.binding"
                  :read-only="isReadOnly"
                  @product-picked="(picked) => onBindingProductPicked(index, picked)"
                />
              </td>
            </tr>
            </template>
          </tbody>
        </table>
        <div v-if="!isReadOnly" class="mt-3">
          <button type="button" @click="addItem" class="text-sm text-blue-600 hover:underline">+ Добавить строку</button>
        </div>
      </section>

      <!-- Влияние на продукт -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <label class="block text-sm font-semibold text-gray-800 mb-1">Влияние на продукт</label>
        <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.influence || '—' }}</span>
        <textarea
          v-else
          v-model="doc.influence"
          rows="2"
          placeholder="На какие продукты/заказы влияет КИ"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        ></textarea>
      </section>

      <!-- Оценка рисков -->
      <section class="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div>
          <label class="block text-sm font-semibold text-gray-800 mb-1">Оценка рисков</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.risk_assessment || '—' }}</span>
          <textarea
            v-else
            v-model="doc.risk_assessment"
            rows="2"
            placeholder="Оцените риски использования продукта / материалов и укажите общий риск влияния дальнейший продукт или заказы, общий риск может быть: низкий, средний или высокий. При высоком риске необходимо инициировать отдельную оценку рисков."
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Общий риск</label>
          <div class="flex gap-4">
            <label v-for="opt in riskOptions" :key="opt.value" class="inline-flex items-center gap-1 text-sm">
              <input type="radio" :value="opt.value" v-model="doc.risk_level" :disabled="isReadOnly" />
              {{ opt.label }}
            </label>
          </div>
        </div>
      </section>

      <!-- Действия для закрытия КИ -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <label class="block text-sm font-semibold text-gray-800 mb-1">Действия для закрытия КИ</label>
        <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.closing_actions || '—' }}</span>
        <textarea
          v-else
          v-model="doc.closing_actions"
          rows="2"
          placeholder="Укажите, какие действия необходимо выполнить для закрытия КИ"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        ></textarea>
      </section>

      <!-- Согласование -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <h2 class="font-semibold text-gray-800 mb-3">Согласование</h2>
        <table class="w-full text-sm border border-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-2 py-1 text-left border-b">ФИО</th>
              <th class="px-2 py-1 text-left border-b w-1/3">Позиция</th>
              <th class="px-2 py-1 text-left border-b w-44">Дата одобрения</th>
              <th class="px-2 py-1 border-b w-10"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(approver, index) in approvers" :key="index" class="border-t border-gray-200">
              <td class="px-2 py-1 align-middle">
                <div v-if="index !== 0 && !isReadOnly" class="v-select-filter" style="width: 100%; flex: none;">
                  <v-select
                    v-model="approver.user_id"
                    :options="users"
                    :reduce="u => u.id"
                    :get-option-label="userName"
                    placeholder="Выберите пользователя"
                  />
                </div>
                <span v-else>{{ approverName(approver) }}</span>
              </td>
              <td class="px-2 py-1 align-middle">
                <input
                  v-if="index !== 0 && !isReadOnly"
                  v-model="approver.position_text"
                  type="text"
                  class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span v-else>{{ approver.position_text || '—' }}</span>
              </td>
              <td class="px-2 py-1 align-middle">
                <span
                  v-if="approver.approved_at"
                  class="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700"
                >
                  Одобрено {{ formatDate(approver.approved_at) }}
                </span>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td class="px-2 py-1 align-middle text-center">
                <button
                  v-if="index !== 0 && !isReadOnly"
                  type="button"
                  @click="removeApprover(index)"
                  class="text-red-600 hover:text-red-800"
                  title="Удалить строку"
                >
                  ✕
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!isReadOnly" class="mt-3">
          <button type="button" @click="addApprover" class="text-sm text-blue-600 hover:underline">+ Добавить согласующего</button>
        </div>
      </section>

      <!-- Закрытие КИ -->
      <section class="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <h2 class="font-semibold text-gray-800">Закрытие КИ</h2>

        <div>
          <label class="block text-sm text-gray-700 mb-1">Выполнение действий для закрытия</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.closing_execution || '—' }}</span>
          <textarea
            v-else
            v-model="doc.closing_execution"
            rows="2"
            placeholder="Укажите, все ли действия для закрытия выполнены, дайте ссылки на подтверждающие документы и приложите их к КИ."
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>

        <div>
          <label class="block text-sm text-gray-700 mb-1">Требуется ли инициирование расследования</label>
          <div class="flex gap-4">
            <label class="inline-flex items-center gap-1 text-sm">
              <input type="radio" :value="true" v-model="doc.investigation_required" :disabled="isReadOnly" />
              Да
            </label>
            <label class="inline-flex items-center gap-1 text-sm">
              <input type="radio" :value="false" v-model="doc.investigation_required" :disabled="isReadOnly" />
              Нет
            </label>
          </div>
        </div>

        <template v-if="doc.investigation_required === true">
          <div>
            <label class="block text-sm text-gray-700 mb-1">Номер расследования</label>
            <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ doc.investigation_number || '—' }}</span>
            <input
              v-else
              v-model="doc.investigation_number"
              type="text"
              placeholder="РН-..."
              class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label class="block text-sm text-gray-700 mb-1">Причина инициирования расследования</label>
            <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.investigation_reason || '—' }}</span>
            <textarea
              v-else
              v-model="doc.investigation_reason"
              rows="2"
              placeholder="Укажите причину инициирования расследования или напишите Н/П"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            ></textarea>
          </div>
        </template>
      </section>

      <!-- Кнопки действий -->
      <div class="flex flex-wrap gap-2 justify-end">
        <button
          v-if="canSaveDraft"
          v-can-write
          type="button"
          @click="saveDraft"
          :disabled="saving"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50"
        >
          {{ saving ? 'Сохранение...' : (doc.status === 'draft' ? 'Сохранить как черновик' : 'Сохранить') }}
        </button>
        <button
          v-if="canSendForApproval"
          v-can-write
          type="button"
          @click="sendForApproval"
          :disabled="saving"
          class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-medium disabled:opacity-50"
        >
          Отправить на согласование
        </button>
        <button
          v-if="canApprove"
          v-can-write
          type="button"
          @click="approveDoc"
          :disabled="saving"
          class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium disabled:opacity-50"
        >
          Одобрить
        </button>
        <button
          v-if="canClose"
          v-can-write
          type="button"
          @click="confirmCloseShow = true"
          :disabled="saving"
          class="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded font-medium disabled:opacity-50"
        >
          Завершить
        </button>
      </div>
    </div>

    <!-- Модалка подтверждения завершения -->
    <div v-if="confirmCloseShow" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="confirmCloseShow = false">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h3 class="text-lg font-semibold mb-3">Завершение документа</h3>
        <p class="text-sm text-gray-700 mb-5">Завершить документ? После завершения форма станет доступна только для чтения.</p>
        <div class="flex justify-end gap-2">
          <button type="button" class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300" @click="confirmCloseShow = false">Нет</button>
          <button type="button" class="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded font-medium" @click="confirmCloseShow = false; closeDoc()">Завершить</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useAuthStore } from '@/stores/auth.js';
import ProductBindingSelector from '@/components/smkDesk/ProductBindingSelector.vue';
import { emptyBinding, bindingFromRow, bindingToPayload } from '@/components/smkDesk/productBinding.js';

export default {
  components: { ProductBindingSelector },
  props: {
    moduleId: { type: [String, Number], required: true },
    controlledId: { type: [String, Number], default: null },
    mode: { type: String, default: 'create' },
  },
  data() {
    return {
      docId: this.controlledId ? Number(this.controlledId) : null,
      localMode: this.mode,
      loading: false,
      saving: false,
      confirmCloseShow: false,
      currentUser: null,
      creator: null,
      users: [],
      doc: this.emptyDoc(),
      items: [],
      approvers: [],
      productResults: [],
      productLoading: false,
      productHasMore: false,
      productPage: 1,
      productQuery: '',
      riskOptions: [
        { value: 'низкий', label: 'Низкий' },
        { value: 'средний', label: 'Средний' },
        { value: 'высокий', label: 'Высокий' },
      ],
    };
  },
  computed: {
    isReadOnly() {
      // approved и closed — форма только для чтения (правка запрещена)
      return ['approved', 'closed'].includes(this.doc.status);
    },
    initiatedDisplay() {
      // На странице создания (initiated_at ещё нет) показываем сегодняшнюю дату
      return this.doc.initiated_at ? this.formatDate(this.doc.initiated_at) : this.formatDate(new Date());
    },
    // Из какого события создан документ — подпись приходит с бэкенда, ключ на экран не выводим
    originDisplay() {
      return this.doc.origin_label || 'Заведён вручную';
    },
    isCreator() {
      return this.currentUser && this.creator && this.currentUser.id === this.creator.id;
    },
    myApprover() {
      if (!this.currentUser) return null;
      return this.approvers.find(a => a.user_id === this.currentUser.id) || null;
    },
    canSaveDraft() {
      // Сохранять можно во всех статусах кроме закрытого
      return !this.isReadOnly;
    },
    canSendForApproval() {
      return this.docId && this.doc.status === 'draft' && this.isCreator;
    },
    canApprove() {
      // Только если строка согласующего сохранена в БД (есть id) — иначе approve по несохранённой строке вернёт 403
      return this.doc.status === 'in_approval' && this.myApprover && this.myApprover.id && !this.myApprover.approved_at;
    },
    canClose() {
      return this.docId && this.doc.status === 'approved' && this.isCreator;
    },
  },
  mounted() {
    this.currentUser = useAuthStore().currentUser;
    this.loadUsers();
    if (this.localMode === 'edit' && this.docId) {
      this.loadDocument();
    } else {
      this.initCreate();
    }
  },
  methods: {
    emptyDoc() {
      return {
        number: 'КИ-',
        cause: '',
        initiated_at: null,
        influence: '',
        risk_assessment: '',
        risk_level: '',
        closing_actions: '',
        closing_execution: '',
        investigation_required: null,
        investigation_number: '',
        investigation_reason: '',
        closer_user_id: null,
        status: 'draft',
        origin_label: '',
      };
    },
    initCreate() {
      this.creator = this.currentUser;
      this.doc = this.emptyDoc();
      // «КИ закрыл» не заполняем автоматически — выбирается вручную
      this.items = [];
      this.ensureFirstItem();
      // Прибитая первая строка согласующих — создатель, «Инженер по качеству»
      this.approvers = [{
        id: null,
        user_id: this.currentUser?.id ?? null,
        position_text: 'Инженер по качеству',
        approved_at: null,
      }];
    },
    async loadUsers() {
      try {
        // Только пользователи с доступом к столу СМК — они могут согласовывать/закрывать
        const { data } = await axios.get('/api/smk/controlled-use/users');
        if (data?.success) {
          this.users = data.data || [];
        }
      } catch (e) {
        // Список пользователей не критичен для чтения формы
      }
    },
    async loadDocument() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/api/smk/controlled-use/${this.docId}`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.ensureFirstItem();
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка загрузки документа');
      } finally {
        this.loading = false;
      }
    },
    applyDocument(d) {
      this.docId = d.id;
      this.localMode = 'edit';
      this.creator = d.creator || null;
      this.doc = {
        number: d.number || '',
        cause: d.cause || '',
        initiated_at: d.initiated_at || null,
        influence: d.influence || '',
        risk_assessment: d.risk_assessment || '',
        risk_level: d.risk_level || '',
        closing_actions: d.closing_actions || '',
        closing_execution: d.closing_execution || '',
        investigation_required: d.investigation_required,
        investigation_number: d.investigation_number || '',
        investigation_reason: d.investigation_reason || '',
        closer_user_id: d.closer_user_id || null,
        status: d.status,
        origin_label: d.origin_label || '',
      };
      this.items = (d.items || []).map(i => ({
        id: i.id,
        product_id: i.product_id,
        product_name: i.product_name || '',
        serial_number: i.serial_number || '',
        short_name: i.short_name || '',
        quantity: i.quantity == null ? null : Number(i.quantity),
        selected: i.product_id ? { id: i.product_id, name: i.product_name } : null,
        // Ручной ввод, если есть название без привязки к номенклатуре
        manual: !!(i.product_name && !i.product_id),
        binding: bindingFromRow(i),
      }));
      this.approvers = (d.approvers || []).map(a => ({
        id: a.id,
        user_id: a.user_id,
        position_text: a.position_text || '',
        approved_at: a.approved_at || null,
      }));
    },
    buildPayload() {
      // При создании первую (прибитую) строку согласующих добавляет бэкенд,
      // поэтому исключаем её из payload. При обновлении отправляем все строки.
      const approversSource = this.docId ? this.approvers : this.approvers.slice(1);
      return {
        number: this.doc.number || null,
        cause: this.doc.cause || null,
        influence: this.doc.influence || null,
        risk_assessment: this.doc.risk_assessment || null,
        risk_level: this.doc.risk_level || null,
        closing_actions: this.doc.closing_actions || null,
        closing_execution: this.doc.closing_execution || null,
        investigation_required: this.doc.investigation_required,
        investigation_number: this.doc.investigation_number || null,
        investigation_reason: this.doc.investigation_reason || null,
        items: this.items.map((i, idx) => ({
          id: i.id || undefined,
          product_id: i.product_id || null,
          product_name: i.product_name || null,
          serial_number: i.serial_number || null,
          short_name: i.short_name || null,
          quantity: i.quantity === '' || i.quantity == null ? 0 : i.quantity,
          sort: idx,
          ...bindingToPayload(i.binding),
        })),
        approvers: approversSource
          .filter(a => a.user_id)
          .map((a, idx) => ({
            id: a.id || undefined,
            user_id: a.user_id,
            position_text: a.position_text || null,
            sort: idx,
          })),
      };
    },
    async persist() {
      const payload = this.buildPayload();
      if (!this.docId) {
        const { data } = await axios.post('/api/smk/controlled-use', payload);
        if (data?.success) {
          this.applyDocument(data.data);
          // Сохраняем query (фильтры реестра), чтобы при возврате реестр восстановился
          this.$router.replace({
            path: `/smk-desk/${this.moduleId}/controlled-use/${this.docId}`,
            query: this.$route.query,
          });
          return true;
        }
        return false;
      }
      const { data } = await axios.put(`/api/smk/controlled-use/${this.docId}`, payload);
      if (data?.success) {
        this.applyDocument(data.data);
        return true;
      }
      return false;
    },
    async saveDraft() {
      this.saving = true;
      try {
        if (await this.persist()) {
          this.notifySuccess('Документ сохранён');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка сохранения');
      } finally {
        this.saving = false;
      }
    },
    async sendForApproval() {
      this.saving = true;
      try {
        if (!(await this.persist())) return;
        const { data } = await axios.post(`/api/smk/controlled-use/${this.docId}/send-for-approval`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ отправлен на согласование');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка отправки на согласование');
      } finally {
        this.saving = false;
      }
    },
    async approveDoc() {
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/smk/controlled-use/${this.docId}/approve`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ одобрен');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка одобрения');
      } finally {
        this.saving = false;
      }
    },
    async closeDoc() {
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/smk/controlled-use/${this.docId}/close`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ завершён');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка завершения');
      } finally {
        this.saving = false;
      }
    },
    addItem() {
      this.items.push({ id: null, product_id: null, product_name: '', serial_number: '', short_name: '', quantity: null, selected: null, manual: false, binding: emptyBinding() });
    },
    // Изделие выбрано — подставляем номенклатуру и серийный номер в пустые поля строки
    onBindingProductPicked(index, picked) {
      const item = this.items[index];
      if (!item || !picked) return;
      if (picked.product_id && !item.product_id && !item.manual) {
        item.product_id = picked.product_id;
        item.product_name = picked.product_name || item.product_name;
        item.selected = { id: picked.product_id, name: picked.product_name };
      }
      if (picked.product_name && !item.short_name) item.short_name = picked.product_name;
      if (picked.serial_number) item.serial_number = picked.serial_number;
    },
    ensureFirstItem() {
      // Если таблица пустая и форма редактируема — добавляем первую строку автоматически
      if (!this.isReadOnly && this.items.length === 0) {
        this.addItem();
      }
    },
    toggleManual(index) {
      const item = this.items[index];
      item.manual = !item.manual;
      // Сбрасываем привязку к номенклатуре при любом переключении режима
      item.selected = null;
      item.product_id = null;
      if (!item.manual) {
        // Переход к выбору из номенклатуры — очищаем ручной текст
        item.product_name = '';
      }
    },
    removeItem(index) {
      this.items.splice(index, 1);
    },
    addApprover() {
      this.approvers.push({ id: null, user_id: null, position_text: '', approved_at: null });
    },
    removeApprover(index) {
      if (index === 0) return;
      this.approvers.splice(index, 1);
    },
    productLabel(option) {
      if (!option) return '';
      return option.sku ? `${option.sku} — ${option.name}` : option.name;
    },
    onProductSearch(query, index) {
      // index не используется: открыт всегда один выпадающий список, результат общий
      this.productQuery = query || '';
      this.productPage = 1;
      clearTimeout(this._productTimer);
      this._productTimer = setTimeout(() => this.fetchProducts(false), 300);
    },
    async fetchProducts(append) {
      this.productLoading = true;
      try {
        const { data } = await axios.post('/api/product/getProducts', {
          search: this.productQuery.trim(),
          page: this.productPage,
          per_page: 20,
        });
        const paginator = data?.data || {};
        const rows = paginator.data || [];
        this.productResults = append ? [...this.productResults, ...rows] : rows;
        const current = paginator.current_page || this.productPage;
        const last = paginator.last_page || current;
        this.productHasMore = current < last;
      } catch (e) {
        if (!append) this.productResults = [];
        this.productHasMore = false;
      } finally {
        this.productLoading = false;
      }
    },
    loadMoreProducts() {
      if (this.productLoading || !this.productHasMore) return;
      this.productPage += 1;
      this.fetchProducts(true);
    },
    onProductSelected(index, val) {
      // val — выбранная позиция номенклатуры либо введённая вручную ({ id: null, name })
      this.items[index].selected = val || null;
      this.items[index].product_id = val && val.id ? val.id : null;
      this.items[index].product_name = val ? val.name : '';
      if (val && val.name && !this.items[index].short_name) {
        this.items[index].short_name = val.name;
      }
    },
    goBack() {
      // Возврат к реестру КИ: открываем вкладку «Контролируемое использование»
      // и восстанавливаем фильтры/страницу (из query формы, переданного реестром)
      this.$router.push({
        path: `/smk-desk/${this.moduleId}`,
        query: { ...this.$route.query, tab: 'controlled' },
      });
    },
    userName(user) {
      if (!user) return '—';
      const full = [user.last_name, user.first_name, user.patronymic].filter(Boolean).join(' ');
      return full || user.name || user.email || ('ID ' + user.id);
    },
    approverName(approver) {
      if (approver.user) return this.userName(approver.user);
      const u = this.users.find(x => x.id === approver.user_id);
      if (u) return this.userName(u);
      if (this.creator && this.creator.id === approver.user_id) return this.userName(this.creator);
      return approver.user_id ? ('ID ' + approver.user_id) : '—';
    },
    statusLabel(status) {
      return {
        draft: 'Черновик',
        in_approval: 'На согласовании',
        approved: 'Одобрено всеми',
        closed: 'Закрыт',
      }[status] || status;
    },
    statusClass(status) {
      return {
        draft: 'bg-gray-100 text-gray-700',
        in_approval: 'bg-yellow-100 text-yellow-700',
        approved: 'bg-green-100 text-green-700',
        closed: 'bg-blue-100 text-blue-700',
      }[status] || 'bg-gray-100 text-gray-700';
    },
    formatDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '—';
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    },
    notifySuccess(message) {
      if (this.$root?.$toast?.success) {
        this.$root.$toast.success(message);
      }
    },
    notifyError(error, fallback) {
      const msg = error?.response?.data?.message || fallback || 'Ошибка';
      if (this.$root?.$toast?.error) {
        this.$root.$toast.error(msg);
      } else {
        alert(msg);
      }
    },
  },
};
</script>
